import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Box, Typography, Card, Chip, IconButton, CardContent, Grid, Paper, Container, Tooltip as TooltipCustom, useMediaQuery, Backdrop, CircularProgress, useTheme, Stack } from '@mui/material';
import { Menu as MenuIcon, Help as HelpIcon } from '@mui/icons-material';
import { useMaterialUIController, setMiniSidenav } from "context";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import SouthIcon from '@mui/icons-material/South';
import FilterPanelContainer from "components/Filter/Filterpopup"
import KpiMetricscards from "components/KPI/KpiMetricscards";
import TimeSeriescards from "components/TimeSeries/TimeSeriescards";
import HighlightsCards from "components/Highlights/Highlightscard";
import Visualizations from "components/Visualizations/Visualizations";
import PieLegendCard from "components/Visualizations/Piecharts"
import Map from 'components/Visualizations/Map'
import Barcharts from "components/Visualizations/Barcharts"
import LineChartSubBrand from "components/Visualizations/Linechartsubbrand";
import TopSkuBarChart from "components/Visualizations/SkuBarchart";
import DoubleBarChart from "components/Visualizations/DoubleBarcharts";
import BrandPriceDumbbellChart from "components/Visualizations/DumbleBarchart";
import datadrilldown from "assets/images/selectedicons/DataDrillDown.svg";
import drillDownArrow from "assets/images/selectedicons/DrillDownArrow.svg";
import datadrillhover from 'assets/images/selectedicons/Data Drill Down1.svg'
import FilterChip from "components/FilterChip/FilterChip";

const CommercialDashboard = ({ subcategoryToken: propSubcategoryToken }) => {

  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav } = controller;
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // Below 900px
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg')); // Between 900px and 1200px

  // State for API data
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    const header = document.querySelector('.MuiAppBar-root');
    if (header) {
      header.style.display = 'none';
    }
  }, []);
  // Toggle sidenav from the page menu icon
  const isTogglingSidenavRef = useRef(false);
  const handleToggleSidenav = useCallback(() => {
    if (isTogglingSidenavRef.current) return;
    isTogglingSidenavRef.current = true;
    // Defer to next frame to avoid blocking heavy charts paint
    requestAnimationFrame(() => {
      setMiniSidenav(dispatch, !miniSidenav);
      setTimeout(() => {
        isTogglingSidenavRef.current = false;
      }, 150);
    });
  }, [dispatch, miniSidenav]);

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
      } catch (error) {
        console.error('Failed loading static mocks or transforming data:', error);
        setError('Failed to load static mock data. Check competitive_analysis/data/allMocks.json');
      } finally {
        setLoading(false);
      }
    };
    fetchAllData();
  }, [location.search, propSubcategoryToken]);


  // first section KPIs card 
  const kpicardsdata = [
    {
      "title": "Rank in Market (Dollars Sold)",
      "values": [
        { "label": "MTD", "change": "1", "value": "#3", "status": "increase" },
        { "label": "QTD", "change": "1", "value": "#3", "status": "nochange" },
        { "label": "YTD", "change": "1", "value": "#4", "status": "decrease" }
      ]
    },
    {
      "title": "Market Share % (Dollars Sold)",
      "values": [
        { "label": "MTD", "change": "1.1%", "value": "8.9%", "status": "increase" },
        { "label": "QTD", "change": "2.8%", "value": "9.1%", "status": "nochange" },
        { "label": "YTD", "change": "3.4%", "value": "9.4%", "status": "nochange" }
      ]
    },
    {
      "title": "Reported Dollars Sold",
      "values": [
        { "label": "MTD", "change": "0.6%", "value": "$82.5M", "status": "increase" },
        { "label": "QTD", "change": "1.2%", "value": "$265.4M", "status": "nochange" },
        { "label": "YTD", "change": "-1.1%", "value": "$892.1M", "status": "decrease" }
      ]
    },
    {
      "title": "Sales Growth Rate (Dollars Sold)",
      "values": [
        { "label": "MTD", "change": "1.3%", "value": "4.9%", "status": "increase" },
        { "label": "QTD", "change": "3.4%", "value": "5.7%", "status": "nochange" },
        { "label": "YTD", "change": "5.2%", "value": "6.3%", "status": "nochange" }
      ]
    },
    {
      "title": "ACV (All Commodity Volume)",
      "values": [
        { "label": "MTD", "change": "0.7%", "value": "82.1%", "status": "increase" },
        { "label": "QTD", "change": "2.1%", "value": "82.9%", "status": "nochange" },
        { "label": "YTD", "change": "3.5%", "value": "84.3%", "status": "nochange" }
      ]
    },
    {
      "title": "Average Price per Unit",
      "values": [
        { "label": "MTD", "change": "0.4%", "value": "$3.98", "status": "increase" },
        { "label": "QTD", "change": "1.8%", "value": "$4.07", "status": "increase" },
        { "label": "YTD", "change": "-3.2%", "value": "$3.92", "status": "decrease" }
      ]
    },
    {
      "title": "% Dollars Sold on Promotion",
      "values": [
        { "label": "MTD", "change": "0.6%", "value": "14.8%", "status": "increase" },
        { "label": "QTD", "change": "1.1%", "value": "15.6%", "status": "nochange" },
        { "label": "YTD", "change": "1.9%", "value": "16.1%", "status": "nochange" }
      ]
    },
    {
      "title": "% Dollars Sold on Display",
      "values": [
        { "label": "MTD", "change": "0.5%", "value": "11.2%", "status": "increase" },
        { "label": "QTD", "change": "1.2%", "value": "12.1%", "status": "nochange" },
        { "label": "YTD", "change": "2.1%", "value": "12.9%", "status": "nochange" }
      ]
    },
    {
      "title": "Innovation Rate (Dollars Sold)",
      "values": [
        { "label": "MTD", "change": "0.3%", "value": "19.1%", "status": "increase" },
        { "label": "QTD", "change": "0.9%", "value": "19.8%", "status": "nochange" },
        { "label": "YTD", "change": "1.5%", "value": "20.4%", "status": "nochange" }
      ]
    }
  ]

  //second section time series cards
  const timeSeriesCardsdata = [
    {
      title: "Rank in Market (Dollars Sold)",
      values: [
        { label: "Nov 2024", change: "+1", value: "#4", status: "increase", arrow: "right" },
        { label: "Nov 2025", change: "+0", value: "#3", status: "nochange", arrow: null },
        { label: "Oct 2025", change: "+0", value: "#3", status: "increase", arrow: "left" }
      ]
    },
    {
      title: "Market Share % (Dollars Sold)",
      values: [
        { label: "Nov 2024", change: "+0.2%", value: "8.7%", status: "increase", arrow: "right" },
        { label: "Nov 2025", change: "+2.9%", value: "8.9%", status: "nochange", arrow: null },
        { label: "Oct 2025", change: "-0.3%", value: "9.2%", status: "decrease", arrow: "left" }
      ]
    },
    {
      title: "Reported Dollars Sold",
      values: [
        { label: "Nov 2024", change: "+2%", value: "$80.9M", status: "increase", arrow: "right" },
        { label: "Nov 2025", change: "+2.9%", value: "$82.5M", status: "nochange", arrow: null },
        { label: "Oct 2025", change: "-1.1%", value: "$83.4M", status: "decrease", arrow: "left" }
      ]
    },
    {
      title: "Sales Growth Rate (Dollars Sold)",
      values: [
        { label: "Nov 2024", change: "+0.1%", value: "4.8%", status: "increase", arrow: "right" },
        { label: "Nov 2025", change: "+2.0%", value: "4.9%", status: "nochange", arrow: null },
        { label: "Oct 2025", change: "-0.2%", value: "5.1%", status: "decrease", arrow: "left" }
      ]
    },
    {
      title: "ACV (All Commodity Volume)",
      values: [
        { label: "Nov 2024", change: "+1.3%", value: "80.8%", status: "increase", arrow: "right" },
        { label: "Nov 2025", change: "+0.6%", value: "82.1%", status: "nochange", arrow: null },
        { label: "Oct 2025", change: "+0.5%", value: "81.6%", status: "increase", arrow: "left" }
      ]
    },
    {
      title: "Average Price per Unit",
      values: [
        { label: "Nov 2024", change: "+6.4%", value: "$3.74", status: "increase", arrow: "right" },
        { label: "Nov 2025", change: "+3.0%", value: "$3.98", status: "nochange", arrow: null },
        { label: "Oct 2025", change: "+2.8%", value: "$3.87", status: "increase", arrow: "left" }
      ]
    },
    {
      title: "% Dollars Sold on Promotion",
      values: [
        { label: "Nov 2024", change: "+0.1%", value: "14.7%", status: "increase", arrow: "right" },
        { label: "Nov 2025", change: "-0.8%", value: "14.8%", status: "nochange", arrow: null },
        { label: "Oct 2025", change: "-1.5%", value: "16.3%", status: "decrease", arrow: "left" }
      ]
    },
    {
      title: "% Dollars Sold on Display",
      values: [
        { label: "Nov 2024", change: "+1.0%", value: "11.1%", status: "increase", arrow: "right" },
        { label: "Nov 2025", change: "-3.8%", value: "12.1%", status: "nochange", arrow: null },
        { label: "Oct 2025", change: "-2.5%", value: "13.6%", status: "decrease", arrow: "left" }
      ]
    },
    {
      title: "Innovation Rate (Dollars Sold)",
      values: [
        { label: "Nov 2024", change: "+1.1%", value: "18.0%", status: "increase", arrow: "right" },
        { label: "Nov 2025", change: "+2.0%", value: "19.1%", status: "nochange", arrow: null },
        { label: "Oct 2025", change: "+0.4%", value: "18.7%", status: "increase", arrow: "left" }
      ]
    }
  ];
  // third section highlights card 
  const highlightCards = [
    // --- Sub Brand by Dollars Sold ---
    {
      title: "#1 Sub Brand by Dollars Sold",
      topLeftLabel: "UNITS SOLD",
      topLeftValue: "105.7 M",
      topRightLabel: "NET SALES",
      topRightValue: "$485.3 M",
      bigTitle: "C4 Performance",
      bottomLabel: "UNITS SOLD LY",
      bottomValue: "97.3 M"
    },
    {
      title: "#2 Sub Brand by Dollars Sold",
      topLeftLabel: "UNITS SOLD",
      topLeftValue: "42.0 M",
      topRightLabel: "NET SALES",
      topRightValue: "$105.0 M",
      bigTitle: "Bloom Sparkling Energy",
      bottomLabel: "UNITS SOLD LY",
      bottomValue: "36.5 M"
    },
    {
      title: "#3 Sub Brand by Dollars Sold",
      topLeftLabel: "UNITS SOLD",
      topLeftValue: "28.4 M",
      topRightLabel: "NET SALES",
      topRightValue: "$72.6 M",
      bigTitle: "XTEND",
      bottomLabel: "UNITS SOLD LY",
      bottomValue: "25.2 M"
    },

    // --- Distributor by Reported Sales ---
    {
      title: "#1 Distributor by Reported Sales",
      topLeftLabel: "UNITS SOLD",
      topLeftValue: "245.0 M",
      topRightLabel: "NET SALES",
      topRightValue: "$510.3 M",
      bigTitle: "The American Bottling Company",
      bottomLabel: "UNITS SOLD LY",
      bottomValue: "228.4 M"
    },
    {
      title: "#2 Distributor by Reported Sales",
      topLeftLabel: "UNITS SOLD",
      topLeftValue: "86.2 M",
      topRightLabel: "NET SALES",
      topRightValue: "$172.3 M",
      bigTitle: "Watkins Distribution",
      bottomLabel: "UNITS SOLD LY",
      bottomValue: "79.4 M"
    },
    {
      title: "#3 Distributor by Reported Sales",
      topLeftLabel: "UNITS SOLD",
      topLeftValue: "72.5 M",
      topRightLabel: "NET SALES",
      topRightValue: "$148.4 M",
      bigTitle: "Watkins Distribution",
      bottomLabel: "UNITS SOLD LY",
      bottomValue: "68.1 M"
    },

    // --- Retailer by Reported Sales ---
    {
      title: "#1 Retailer by Reported Sales",
      topLeftLabel: "UNITS SOLD",
      topLeftValue: "128.0 M",
      topRightLabel: "NET SALES",
      topRightValue: "$262.5 M",
      bigTitle: "Walmart",
      bottomLabel: "UNITS SOLD LY",
      bottomValue: "121.4 M"
    },
    {
      title: "#2 Retailer by Reported Sales",
      topLeftLabel: "UNITS SOLD",
      topLeftValue: "22.9 M",
      topRightLabel: "NET SALES",
      topRightValue: "$91.4 M",
      bigTitle: "GNC",
      bottomLabel: "UNITS SOLD LY",
      bottomValue: "21.0 M"
    },
    {
      title: "#3 Retailer by Reported Sales",
      topLeftLabel: "UNITS SOLD",
      topLeftValue: "18.5 M",
      topRightLabel: "NET SALES",
      topRightValue: "$76.2 M",
      bigTitle: "7-Eleven",
      bottomLabel: "UNITS SOLD LY",
      bottomValue: "17.3 M"
    }
  ];


  const [openFilters, setOpenFilters] = useState(false);
  const handleToggleFilters = () => {
    setOpenFilters((prev) => !prev);
  };

  // Filter state
  const [activeFilter, setActiveFilter] = useState('All');
  const [activeIdx, setActiveIdx] = React.useState(0);
  const [filterLoading, setFilterLoading] = useState(false);

  // Define filter presets for each quick filter
  const getFilterPreset = useCallback((filterName) => {
    const allCompanies = ["Nutrabolt", "Bloom"];
    const allCategories = ["Functional Beverage", "Active Nutrition"];
    const allBrands = ["C4", "Cellucor", "XTEND", "Bloom"];
    const allSubBrands = ["C4", "C4 Performance", "C4 Smart Energy", "C4 Ultimate", "XTEND", "Bloom Sparkling Energy", "Bloom Pop"];
    const allRoutes = ["Ecommerce", "Direct", "DSD"];
    const allChannels = ["Ecommerce", "Convenience Chain", "Convenience Independent", "Drug", "Food", "Multi Outlet", "Mass"];

    switch (filterName) {
      case 'All':
        return {
          Company: allCompanies,
          Category: allCategories,
          Brand: allBrands,
          "Sub-Brand": allSubBrands,
          "Route to Market": allRoutes,
          Channel: allChannels,
        };
      case 'Nutrabolt':
        return {
          Company: ["Nutrabolt"],
          Category: allCategories,
          Brand: ["C4", "Cellucor", "XTEND"],
          "Sub-Brand": ["C4", "C4 Performance", "C4 Smart Energy", "C4 Ultimate", "XTEND"],
          "Route to Market": allRoutes,
          Channel: allChannels,
        };
      case 'Bloom':
        return {
          Company: ["Bloom"],
          Category: ["Functional Beverage"],
          Brand: ["Bloom"],
          "Sub-Brand": ["Bloom Sparkling Energy", "Bloom Pop"],
          "Route to Market": allRoutes,
          Channel: allChannels,
        };
      case 'Functional Beverage':
        return {
          Company: allCompanies,
          Category: ["Functional Beverage"],
          Brand: ["C4", "Bloom"],
          "Sub-Brand": ["C4 Performance", "C4 Smart Energy", "C4 Ultimate", "Bloom Sparkling Energy", "Bloom Pop"],
          "Route to Market": allRoutes,
          Channel: allChannels,
        };
      case 'Active Nutrition':
        return {
          Company: ["Nutrabolt"],
          Category: ["Active Nutrition"],
          Brand: ["C4", "Cellucor", "XTEND"],
          "Sub-Brand": ["C4", "XTEND"],
          "Route to Market": allRoutes,
          Channel: allChannels,
        };
      default:
        return null;
    }
  }, []);

  const [filterPresets, setFilterPresets] = useState(() => getFilterPreset('All'));

  // Handle filter click with full-page loader
  const handleFilterClick = (filter) => {
    if (filter === activeFilter || filterLoading) return;

    setFilterLoading(true);
    setLoading(true); // Show full-page loader

    // Simulate loading delay
    setTimeout(() => {
      const preset = getFilterPreset(filter);
      setActiveFilter(filter);

      // Set the preset - this will trigger FilterPanelContainer to apply it
      if (preset) {
        setFilterPresets(preset);
      } else {
        setFilterPresets(null);
      }

      setFilterLoading(false);
      setLoading(false); // Hide full-page loader

      // Open filter panel for "Custom" filter
      if (filter === 'Custom') {
        setOpenFilters(true);
      }
    }, 800);
  };

  // Handle filter changes from FilterPanelContainer
  const handleFiltersChange = useCallback((newFilters) => {
    // Don't check for preset match if we're already in Custom mode
    // FilterPanelContainer's isApplyingPresetRef prevents callback when applying presets
    if (activeFilter === 'Custom') return;

    // Check if the current filters match any preset
    const presetNames = ['All', 'Nutrabolt', 'Bloom', 'Functional Beverage', 'Active Nutrition'];
    let matchedPreset = null;

    for (const presetName of presetNames) {
      const preset = getFilterPreset(presetName);
      if (preset) {
        const isMatching = Object.keys(preset).every(key => {
          const presetValues = [...(preset[key] || [])].sort();
          const currentValues = [...(newFilters[key] || [])].sort();
          return presetValues.length === currentValues.length &&
            presetValues.every((val, idx) => val === currentValues[idx]);
        });

        if (isMatching) {
          matchedPreset = presetName;
          break;
        }
      }
    }

    // If filters don't match any preset, switch to Custom
    if (!matchedPreset && activeFilter !== 'Custom') {
      setActiveFilter('Custom');
      setFilterPresets(null);
    }
  }, [activeFilter, getFilterPreset]);

  const kpiBriefData = [
    {
      title: "Month",
      value: "$ 65.2 M",
      lyValue: "59.12 M",
      pct: "5.8%",
      positive: true,
      status: "increase",
      glvalue: "$ 85.5 M"
    },
    {
      title: "Quarter",
      value: "$ 180.6 M",
      lyValue: "160.29 M",
      pct: "11.9%",
      positive: true,
      status: "increase",
      glvalue: "$ 207.3 M"
    },
    {
      title: "Year",
      value: "$ 859.1 M",
      lyValue: "823.03 M",
      pct: "5.6%",
      positive: true,
      status: "increase",
      glvalue: "$ 882.8 M"
    }
  ];

  const brands = [

    { "label": "Cellucor", "value": 10, "color": "#747474" },
    { "label": "XTEND", "value": 8, "color": "#C5C5C5" },
    { "label": "Bloom", "value": 18, "color": "#C393E4" },
    { "label": "C4", "value": 64, "color": "#ECD01C" },
  ]

  const subBrands = [
    { "label": "C4 Powder", "value": 8, "color": "#C2CF2D" },
    { "label": "Bloom Pop", "value": 6, "color": "#59A671" },
    { "label": "Bloom Sparkling Energy", "value": 12, "color": "#C393E4" },
    { "label": "XTEND", "value": 8, "color": "#C5C5C5" },
    { "label": "Cellucor", "value": 10, "color": "#747474" },
    { "label": "C4 Ultimate", "value": 6, "color": "#1A1A1A" },
    { "label": "C4 Smart Energy", "value": 5, "color": "#1BA4E6" },
    { "label": "C4 Performance", "value": 45, "color": "#ECD01C" },
  ]
  const competitors = [
    { "label": "Monster", "value": 25, "color": "#95C11F" },
    { "label": "Celsius", "value": 7, "color": "#F37021" },
    { "label": "Alani", "value": 4, "color": "#00C3DA" },
    { "label": "Ghost", "value": 3, "color": "#F9005A" },
    { "label": "Rockstar", "value": 3, "color": "#FFB300" },
    { "label": "NOS", "value": 3, "color": "#0066CC" },
    { "label": "C4", "value": 3, "color": "#ECD01C" },
    { "label": "Bloom", "value": 2, "color": "#C393E4" },
    { "label": "Others", "value": 15, "color": "#000000" },
    { "label": "Red Bull", "value": 32, "color": "#DA291C" },
  ]
  const regions = [
    { "label": "West", "value": 18, "color": "#3BA73B" },
    { "label": "North", "value": 7, "color": "#B5CBEA" },
    { "label": "South", "value": 17, "color": "#B972C2" },
    { "label": "South Central", "value": 20, "color": "#FFC081" },
    { "label": "Northeast", "value": 17, "color": "#D93737" },
    { "label": "Central", "value": 30, "color": "#DEDE96" },

  ]

  const regionChartData = {
    title: "Reported Dollar Sales by Region",

    xLabels: [
      "Central",
      "North",
      "Northeast",
      "South",
      "South Central",
      "West"
    ],

    series: [
      { label: "C4 Powder", color: "#C2CF2D" },
      { label: "C4 Performance", color: "#ECD01C" },
      { label: "C4 Smart Energy", color: "#1BA4E6" },
      { label: "C4 Ultimate", color: "#1A1A1A" },
      { label: "Cellucor", color: "#747474" },
      { label: "XTEND", color: "#C5C5C5" },
      { label: "Bloom Sparkling Energy", color: "#C393E4" },
      { label: "Bloom Pop", color: "#59A671" }
    ],

    dataMatrix: [
      [13.78, 63.17, 4.59, 10.34, 11.49, 9.19, 6.89, 5.74],
      [17.49, 74.95, 6.25, 12.49, 14.99, 11.24, 8.74, 6.25],
      [14.74, 65.78, 4.54, 10.21, 12.48, 10.21, 7.94, 6.81],
      [21.48, 83.22, 6.71, 14.77, 16.11, 13.42, 10.74, 8.05],
      [27.08, 112.85, 9.03, 18.06, 21.06, 16.55, 12.04, 10.53],
      [22.00, 95.33, 7.33, 16.13, 19.07, 14.67, 10.27, 8.80]
    ]

  };

  const lineChartSubBrandData = {
    title: "Reported Dollars Sold by Sub Brand",

    xLabels: [
      "Dec. 2025", "Jan. 2025", "Feb. 2025", "Mar. 2025",
      "Apr. 2025", "May. 2025", "Jun. 2025", "Jul. 2025",
      "Aug. 2025", "Sep. 2025", "Oct. 2025", "Nov. 2025"
    ],

    series: [
      { label: "C4 Powder", color: "#C2CF2D" },
      { label: "C4 Performance", color: "#ECD01C" },
      { label: "C4 Smart Energy", color: "#1BA4E6" },
      { label: "C4 Ultimate", color: "#1A1A1A" },
      { label: "Cellucor", color: "#747474" },
      { label: "XTEND", color: "#C5C5C5" },
      { label: "Bloom Sparkling Energy", color: "#C393E4" },
      { label: "Bloom Pop", color: "#59A671" }
    ],

    //  NOW supports ACTUAL + FORECAST
    dataMatrix: {
      actual: [
        [22, 40, 12, 14, 16, 10, 5, 8],
        [24, 44, 13, 16, 17, 11, 7, 9],
        [28, 52, 15, 18, 20, 13, 8, 11],
        [27, 50, 14, 17, 22, 14, 10, 12],
        [32, 58, 16, 18, 18, 16, 9, 14],
        [35, 62, 18, 20, 28, 18, 12, 16],
        [30, 65, 17, 19, 24, 17, 11, 15],
        [28, 59, 16, 18, 22, 16, 10, 14],
        [31, 61, 17, 21, 25, 19, 13, 16],
        [29, 63, 19, 20, 24, 17, 13, 17],
        [30, 68, 20, 22, 26, 20, 15, 18],
        [32, 72, 22, 23, 30, 22, 16, 19]
      ]


    }
  };

  const topSkuChartData = {
    title: "Top 10 Ordered SKUs by Reported Dollars Sold",

    xLabels: [
      "C4 Original – Frozen Bombsicle – 12oz RTD (24-pack)",
      "C4 Smart Energy – Frozen Bombsicle – 16oz Can",
      "Cellucor BCAA Sport – Watermelon – 30srv",
      "XTEND BCAA – Watermelon Explosion – 30srv",
      "C4 Smart Energy – Peach Mango – 12oz",
      "C4 Original – Lemon Lime – 30srv",
      "C4 Ultimate – Midnight Cherry – 16oz",
      "Cellucor BCAA Sport – Blue Razz – 30srv",
      "Bloom Sparkling – Grapefruit – 12oz",
      "Bloom Pop – Blue Razz – 12oz"
    ],

    series: [
      { label: "Dollars Sold - This Period", key: "thisPeriod", color: "#0A3478" },
      { label: "Dollars Sold - Last Period", key: "lastPeriod", color: "#A8C4EA" }
    ],

    dataMatrix: {
      actual: [
        [280, 80],  // SKU 1
        [232, 55],  // SKU 2
        [208, 62],  // SKU 3
        [193, 27],  // SKU 4
        [155, 53],  // SKU 5
        [108, 35],  // SKU 6
        [101, 52],  // SKU 7
        [99, 65],  // SKU 8
        [75, 44],  // SKU 9
        [64, 40]   // SKU 10
      ]
    }
  };

  const bottom10SkuChartData = {
    title: "Bottom 10 Ordered SKUs by Reported Dollars Sold",

    xLabels: [
      "C4 Performance – Blue Razz – 12oz Can",
      "Bloom Pop – Orange – 12oz",
      "Cellucor BCAA Sport – Blue Razz – 30srv",
      "C4 Ultimate – Citrus Blaze – 12oz",
      "C4 Smart Energy – Tropic Blast – 12oz",
      "C4 Extreme – Midnight Cherry – 30srv",
      "C4 Original – Fruit Punch – 60srv",
      "C4 Ripped – Arctic Ice – 30srv",
      "C4 Performance – Strawberry Burst – 16oz",
      "C4 Performance – Orange Slice – 16oz"
    ],

    series: [
      { label: "Dollars Sold - This Period", key: "thisPeriod", color: "#0A3478" },
      { label: "Dollars Sold - Last Period", key: "lastPeriod", color: "#A8C4EA" }
    ],

    dataMatrix: {
      actual: [
        [1, 6],   // 1M vs 6M
        [2, 5],   // 2M vs 5M
        [7, 12],  // 7M vs 12M
        [10, 11], // 10M vs 11M
        [9, 12],  // 9M vs 12M
        [18, 19], // 18M vs 19M
        [20, 24], // 20M vs 24M
        [22, 19], // 22M vs 19M
        [22, 26], // 22M vs 26M
        [24, 31]  // 24M vs 31M
      ]
    }
  };

  const brandSalesChartData = {
    title: "Reported Dollars Sold by Brand",

    xLabels: [
      "Red Bull",
      "Monster",
      "Celsius",
      "Alani",
      "Ghost",
      "C4",
      "Rockstar",
      "NOS",
      "Reign",
      "Bloom"
    ],

    series: [
      { label: "Dollars Sold - Last Period", key: "last", color: "#8EB7DB" },
      { label: "Dollars Sold - This Period", key: "current", color: "#0A3A73" },

      // C4 custom colors
      { label: "C4 - Last Period", key: "last", color: "#D6C85F" },
      { label: "C4 - This Period", key: "current", color: "#EACF2F" },

      // Bloom custom colors
      { label: "Bloom - Last Period", key: "last", color: "#D9C3E9" },
      { label: "Bloom - This Period", key: "current", color: "#C393E4" }
    ],

    dataMatrix: {
      actual: [
        [4.1, 4.6],   // Red Bull (B$)
        [2.9, 3.3],   // Monster
        [1.0, 1.3],   // Celsius
        [0.8, 0.9],   // Alani
        [0.7, 0.8],   // Ghost
        [0.3, 0.4],   // C4 (special color)
        [0.3, 0.4],   // Rockstar
        [0.2, 0.3],   // NOS
        [0.3, 0.2],   // Reign
        [0.1, 0.2]    // Bloom (purple bars)
      ]
    }
  };

  const brandPriceComparison = {
    title: "Brand Base Price vs Promoted Price",

    xLabels: [
      "Red Bull",
      "Bloom",
      "Monster",
      "Alani",
      "Reign",
      "C4",
      "Celsius",
      "Ghost",
      "NOS",
      "Rockstar"
    ],

    // LEGEND CONFIG
    series: [
      { label: "Average Promo Price - Last Period", key: "promoLast", color: "#8C8C8C" },
      { label: "Base Price - Last Period", key: "baseLast", color: "#D8C72C" },
      { label: "Average Promo Price - This Period", key: "promoCurrent", color: "#000000" },
      { label: "Base Price - This Period", key: "baseCurrent", color: "#FFE000" }
    ],

    // DATA MATRIX (ORDER MATTERS)
    dataMatrix: {
      actual: [
        [3.05, 3.60, 3.30, 3.69], // Red Bull
        [2.82, 3.58, 2.56, 3.62], // Bloom
        [3.05, 3.52, 3.18, 3.59], // Monster
        [2.60, 3.20, 2.87, 3.23], // Alani
        [2.51, 3.25, 2.67, 3.22], // Reign
        [2.44, 3.06, 2.64, 3.17], // C4
        [2.50, 3.11, 2.61, 3.16], // Celsius
        [2.35, 2.90, 2.58, 2.99], // Ghost
        [2.37, 3.00, 2.47, 2.98], // NOS
        [2.50, 2.86, 2.46, 2.90]  // Rockstar
      ]
    },

    yTicks: [2.0, 2.5, 3.0, 3.5, 4.0, 4.5]
  };

  const [hover, setHover] = React.useState(false);

  // FINAL DATA (from your reference image)
  const usaData = {
    CA: 140,
    NV: 20,
    AZ: 60,
    OR: 40,
    WA: 70,
    NM: 30,
    TX: 160,
    OK: 50,
    AR: 25,
    LA: 40,
    MS: 35,
    AL: 25,
    GA: 60,
    FL: 55,
    SC: 45,
    NC: 70,
    TN: 50,
    KY: 40,
    VA: 35,
    WV: 20,
    MD: 15,
    PA: 65,
    NY: 75,
    NJ: 30,
    CT: 18,
    MA: 25,
    NH: 12,
    ME: 10,
    MI: 45,
    OH: 40,
    IN: 35,
    IL: 80,
    IA: 15,
    MN: 12,
    WI: 22,
    MO: 60,
    KS: 18,
    NE: 10,
    SD: 8,
    ND: 5,
    CO: 35,
    UT: 22,
    ID: 15,
    MT: 8,
    WY: 6,
    AK: 5,
    HI: 5,
  };

  const thickBorderStates = [
    "CA", // California (east border thick)
    "NV", // Nevada (west border thick)
    "AZ", // Arizona
    "NM", // New Mexico
    "TX", // Texas
    "OK", // Oklahoma
    "MO", // Missouri
    "IL", // Illinois
    "IN", // Indiana
    "OH", // Ohio
    "PA", // Pennsylvania
    "NJ", // New Jersey
    "NY"  // New York
  ];






  const legendData = [
    { label: "$0–$10M", color: "#C8C6C4" },
    { label: "$10M–$30M", color: "#D0EBBE" },
    { label: "$30M–$60M", color: "#B7E29A" },
    { label: "$60–$100M", color: "#99D56F" },
    { label: "$100M–$150M", color: "#6BB542" },
    { label: "$150M+", color: "#4C8C17" }
  ];

  return (

    <Box sx={{
      ml: 0,
      transition: "margin-left 0.25s ease",
      "& .MuiBox-root": { backgroundImage: "none" }
    }} style={{
      padding: isMobile ? '8px' : '0px  16px 0px 16px',
      fontFamily: 'inter !important'
    }}>
      {/* Loading Backdrop */}
      <Backdrop
        open={loading}
        sx={{
          position: 'fixed',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
        }}
      >
        <CircularProgress color="primary" />
      </Backdrop>

      <Box
        sx={{
          height: {
            xs: "calc(100dvh - 140px)", // mobile header
            md: "calc(100vh - 64px)",  // desktop header
          },
          overflowY: "auto",
          // px: isMobile ? 1 : 2,
          pb: "100px",
          /* smooth scrolling */
          scrollBehavior: "smooth",
          WebkitOverflowScrolling: "touch", // iOS momentum

          /* hide scrollbar */
          scrollbarWidth: "none",          // Firefox
          msOverflowStyle: "none",         // IE / Edge
          "&::-webkit-scrollbar": {
            display: "none",               // Chrome / Safari
          },
        }}
      >
        {/* Section Title: KPI's */}
        <Typography sx={{ mb: 1, mt: 3 }}>
          <span style={{ color: '#1672B980', fontSize: '16px', fontWeight: 200 }}>Commercial / </span> <span style={{ fontWeight: 200, fontSize: "16px" }}>KPI's</span>
        </Typography>
        <Box sx={{ width: '100%', height: '1px', background: '#1672B91A', borderRadius: '2px', mb: 0 }} />
        <KpiMetricscards cardsData={kpicardsdata} kpiBriefData={kpiBriefData} />

        {/* Section Title: Time Series */}
        <Typography sx={{ fontSize: '16px', mb: 1, mt: 3 }}>
          <span style={{ color: '#1672B980', fontSize: '16px', fontWeight: 200 }}>Commercial / </span> <span style={{ fontWeight: 200, fontSize: "16px" }}>Time Series</span>
        </Typography>
        <Box sx={{ width: '100%', height: '1px', background: '#1672B91A', borderRadius: '2px', mb: 0 }} />
        <TimeSeriescards timeSeriesCards={timeSeriesCardsdata} />

        {/* Section Title: Highlights with YTD/QTD/MTD buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, mt: 3 }}>
          <Typography sx={{ fontSize: '16px' }}>
            <span style={{ color: '#1672B980', fontWeight: 200, fontSize: "16px" }}>Commercial / </span><span style={{ fontWeight: 200, fontSize: "16px" }}> Highlights</span>
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {['YTD', 'QTD', 'MTD'].map((label, idx) => (
              <FilterChip
                key={label}
                label={label}
                idx={idx}
                activeIdx={activeIdx}
                onClick={setActiveIdx}
              />
            ))}
          </Box>
        </Box>
        <Box sx={{ width: '100%', height: '1px', background: '#1672B91A', borderRadius: '2px', mb: 0 }} />
        <HighlightsCards data={highlightCards} />

        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography sx={{ fontSize: '16px' }}>
              <span style={{ color: "#1672B980", fontSize: '16px', fontWeight: 200 }}>Commercial / </span> <span style={{ fontWeight: 200, fontSize: "16px" }}>Visualizations</span>
            </Typography>

            <Box sx={{ display: 'flex', gap: 1 }}>
              {["YTD", "QTD", "MTD"].map((label, idx) => (
                <FilterChip
                  key={label}
                  label={label}
                  idx={idx}
                  activeIdx={activeIdx}
                  onClick={setActiveIdx}
                />
              ))}
            </Box>
          </Box>

          <Box sx={{ width: '100%', height: '1px', background: '#1672B91A', borderRadius: '2px', mb: 0 }} />
        </Box>
        {/* Section Title: Visualizations */}
        <Grid
          container
          rowSpacing={2}
          columnSpacing={2}
          sx={{
            mt: "26px !important",
            alignItems: "stretch"   // keeps all cards same height
          }}
        >
          <Grid item xs={12} sm={6} md={3} sx={{
            pt: { xs: "0px !important", sm: "0px !important", md: "0px !important" }
          }} >
            <PieLegendCard title="Reported Dollar Sold by Brand" data={brands} />
          </Grid>

          <Grid item xs={12} sm={6} md={3} sx={{
            pt: { xs: "26px !important", sm: "26px !important", md: "0px !important" }
          }} >
            <PieLegendCard title="Reported Dollars Sold by Sub Brand" data={subBrands} />
          </Grid>

          <Grid item xs={12} sm={6} md={3} sx={{
            pt: { xs: "26px !important", sm: "26px !important", md: "0px !important" }
          }}  >
            <PieLegendCard title="Brand Marketshare by Dollars Sold" data={competitors} />
          </Grid>

          <Grid item xs={12} sm={6} md={3} sx={{
            pt: { xs: "26px !important", sm: "26px !important", md: "0px !important" }
          }} >
            <PieLegendCard title="Reported Dollar Sales by Region" data={regions} />
          </Grid>
        </Grid>
        <Grid container spacing={3} sx={{ mt: "10px !important" }}>
          {/* LEFT CARD - USA MAP */}
          <Grid item xs={12} md={4} >
            <Map
              title="Reported Dollar Sales Heatmap by State"
              usaData={usaData}
              thickBorderStates={thickBorderStates}
              legendData={legendData}
            />

          </Grid>

          {/* RIGHT CARD - BAR CHART */}
          <Grid item xs={12} md={8} >
            <Barcharts
              title={regionChartData.title}
              xLabels={regionChartData.xLabels}
              series={regionChartData.series}
              dataMatrix={regionChartData.dataMatrix}
              yTicks={[0, 50, 100, 150, 200, 250]}
              yAxisLabel="Reported Sales ($M)"
              rounded={true}
            />
          </Grid>
        </Grid>
        <Grid container sx={{ mt: "34px !important" }}>
          <Grid item xs={12}>
            <LineChartSubBrand
              title={lineChartSubBrandData.title}
              xLabels={lineChartSubBrandData.xLabels}
              series={lineChartSubBrandData.series}
              dataMatrix={lineChartSubBrandData.dataMatrix}
              yTicks={[0, 15, 30, 45, 60, 75]}
              valueFormatter={(v) => `${v}M`}
              yAxisLabel="Reported Dollars Sold ($M)"
            />
          </Grid>
        </Grid>
        <Grid container spacing={3} sx={{ mt: '34px !important' }}>
          {/* LEFT CARD - USA MAP */}
          <Grid item xs={12} md={6} sx={{
            pt: { xs: "0px !important", sm: "0px !important", }
          }}>
            <TopSkuBarChart
              title={topSkuChartData.title}
              xLabels={topSkuChartData.xLabels}
              series={topSkuChartData.series}
              dataMatrix={topSkuChartData.dataMatrix}
              xTicks={[0, 50, 100, 200, 300]}
            />

          </Grid>

          {/* RIGHT CARD - BAR CHART */}
          <Grid item xs={12} md={6} sx={{
            pt: { xs: "34px !important", sm: "34px !important", md: '0px !important' }
          }}>
            <TopSkuBarChart
              title={bottom10SkuChartData.title}
              xLabels={bottom10SkuChartData.xLabels}
              series={bottom10SkuChartData.series}
              dataMatrix={bottom10SkuChartData.dataMatrix}
              xTicks={[0, 10, 20, 30, 40]}
            />

          </Grid>
        </Grid>

        <Grid container sx={{ mt: "34px !important" }}>
          <Grid item xs={12}>
            <DoubleBarChart
              title={brandSalesChartData.title}
              xLabels={brandSalesChartData.xLabels}
              series={brandSalesChartData.series}
              dataMatrix={brandSalesChartData.dataMatrix}
              yTicks={[0, 1, 2, 3, 4, 5]}
            />
          </Grid>
        </Grid>

        <Grid container sx={{ mt: "34px !important" }}>
          <Grid item xs={12}>
            <BrandPriceDumbbellChart
              title={brandPriceComparison.title}
              xLabels={brandPriceComparison.xLabels}
              series={brandPriceComparison.series}
              dataMatrix={brandPriceComparison.dataMatrix}
              yTicks={brandPriceComparison.yTicks}
            />
          </Grid>
        </Grid>

      </Box>


      {/* <Visualizations /> */}
    </Box>


  );
};

export default CommercialDashboard;